import { z } from "zod";
import { ALLERGEN_CODES } from "./constants";
import { NUTRIENTS } from "./nutrition";

// A product read from its label (docs/BAZA_INGREDIENTE.md, phase 2). The model only proposes what is printed; the
// restaurant checks every field against the label before the product is saved. Values per 100 g (or 100 ml).

/** Photos of one label (front, nutrition table, ingredients): at most this many per reading. */
export const LABEL_MAX_PHOTOS = 4;

const nutrient = z.number().min(0).max(1000).nullable();
const text = (max: number) => z.string().trim().max(max);

export const labelDraftSchema = z.object({
  name: text(200).min(1),
  brand: text(120).nullable(),
  producer: text(200).nullable(),
  quantity: text(60).nullable(),
  /** The barcode digits as printed (EAN-8, EAN-13, UPC). */
  ean: z
    .string()
    .trim()
    .regex(/^\d{8,14}$/)
    .nullable(),
  ingredientsText: text(4000).nullable(),
  allergens: z.array(z.enum(ALLERGEN_CODES)).max(14),
  /** "Poate conține urme de…" */
  traces: z.array(z.enum(ALLERGEN_CODES)).max(14),
  /** E numbers ("E250", "E150a"). */
  additives: z
    .array(
      z
        .string()
        .trim()
        .regex(/^E\d{3,4}[a-z]?$/i)
        .transform((s) => `E${s.slice(1).toLowerCase()}`),
    )
    .max(60),
  frozen: z.boolean(),
  per100: z.object(
    Object.fromEntries(NUTRIENTS.map((n) => [n, nutrient])) as Record<
      (typeof NUTRIENTS)[number],
      typeof nutrient
    >,
  ),
});
export type LabelDraft = z.infer<typeof labelDraftSchema>;

/** Saving a product the restaurant checked against its label. */
export const labelSaveSchema = labelDraftSchema.extend({
  photoIds: z.array(z.string().uuid()).min(1).max(LABEL_MAX_PHOTOS),
});
export type LabelSave = z.infer<typeof labelSaveSchema>;

export const labelReadSchema = z.object({
  photoIds: z.array(z.string().uuid()).min(1).max(LABEL_MAX_PHOTOS),
});
